import type { Timeframe } from './zone-math';

export type Candle = { time: number; open: number; high: number; low: number; close: number; volume?: number };
export type Anchor = { time: number; price: number };
export type ChartDot = Anchor & { id: string; number: number };
// Keep the existing storage slots (1/2) for compatibility, but allow only Z/P.
export const markerName = (number: number) => number === 1 ? 'Z' : 'P';
export function pairDots(dots: ChartDot[]): ChartDot[] {
  const pair = [1, 2].flatMap(number => {
    const dot = dots.find(item => item.number === number);
    return dot ? [dot] : [];
  });
  return pair.length === dots.length && pair.every((dot, i) => dot === dots[i]) ? dots : pair;
}
export type Leg = { start: Anchor; end: Anchor };
export type Scale = { low: number; high: number; top: number; bottom: number };
export const PERIOD: Record<Timeframe, number> = { M1: 60000, M5: 300000, M15: 900000, M30: 1800000, H1: 3600000, H4: 14400000, D1: 86400000 };
export const clamp = (v: number, low: number, high: number) => Math.max(low, Math.min(high, v));
export const priceY = (price: number, s: Scale) => s.top + (s.high - price) / (s.high - s.low) * (s.bottom - s.top);
export const yPrice = (y: number, s: Scale) => s.high - (clamp(y, s.top, s.bottom) - s.top) / (s.bottom - s.top) * (s.high - s.low);

// Preserve the anchor timestamp while mapping it to its containing candle on larger timeframes.
export function timeX(time: number, rows: Candle[], left: number, right: number, timeframe: Timeframe, plotRight = right): number {
  const step = (right - left) / Math.max(1, rows.length);
  if (!rows.length || time >= rows[rows.length - 1].time + PERIOD[timeframe]) return plotRight + step;
  if (time < rows[0].time) return left - step;
  let low = 0, high = rows.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (rows[middle].time <= time) low = middle + 1;
    else high = middle;
  }
  return left + (Math.max(0, low - 1) + .5) * step;
}

export function validateCandles(input: unknown): Candle[] {
  if (!Array.isArray(input) || input.length < 2) throw new Error('At least two OHLC candles are required.');
  const map = new Map<number, Candle>();
  for (const row of input) {
    if (!row || ![row.time, row.open, row.high, row.low, row.close].every(v => typeof v === 'number' && Number.isFinite(v)) ||
      row.time < 0 || row.time > 8640000000000000 || row.low <= 0 || row.low > Math.min(row.open, row.close) || row.high < Math.max(row.open, row.close)) {
      throw new Error('Invalid candle: check timestamps and Open / High / Low / Close prices.');
    }
    map.set(row.time, row);
  }
  const rows = [...map.values()].sort((a, b) => a.time - b.time);
  if (rows.length < 2) throw new Error('The file needs at least two different timestamps.');
  return rows;
}

export function intervalOf(rows: Candle[]) {
  let min = Infinity;
  for (let i = 1; i < rows.length; i++) min = Math.min(min, rows[i].time - rows[i - 1].time);
  return min;
}

export function aggregateCandles(rows: Candle[], timeframe: Timeframe): Candle[] {
  const step = PERIOD[timeframe];
  if (intervalOf(rows) > step) throw new Error('This CSV has larger candles. Choose a larger timeframe or import M1 / M5 data.');
  const buckets = new Map<number, Candle>();
  for (const row of rows) {
    const time = Math.floor(row.time / step) * step;
    const current = buckets.get(time);
    if (!current) buckets.set(time, { ...row, time });
    else { current.high = Math.max(current.high, row.high); current.low = Math.min(current.low, row.low); current.close = row.close; current.volume = (current.volume || 0) + (row.volume || 0); }
  }
  return [...buckets.values()];
}

function parseTime(value: string): number {
  if (/^\d{10,13}$/.test(value)) return Number(value) * (value.length <= 10 ? 1000 : 1);
  const date = value.replace(/^(\d{4})[./](\d{2})[./](\d{2})/, '$1-$2-$3').replace(' ', 'T');
  // MT timestamps have no timezone: display broker clock as-is, without silently shifting it.
  return Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(date) ? date : date + 'Z');
}

export function parseCsv(text: string): Candle[] {
  if (text.length > 10_000_000) throw new Error('CSV is too large. Import a file under 10 MB.');
  const lines = text.replace(/^\uFEFF/, '').trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 3) throw new Error('CSV needs a header and at least two candles.');
  const separator = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
  const split = (line: string) => line.split(separator).map(v => v.trim().replace(/^"|"$/g, ''));
  const header = split(lines[0]).map(v => v.toLowerCase().replace(/[<>\s_]/g, ''));
  const open = header.indexOf('open'), high = header.indexOf('high'), low = header.indexOf('low'), close = header.indexOf('close');
  const date = header.indexOf('date');
  const time = header.findIndex(v => ['timestamp', 'datetime', 'time'].includes(v));
  if ([open, high, low, close].some(v => v < 0) || (date < 0 && time < 0)) throw new Error('Use MT5 CSV or columns: time, open, high, low, close.');
  const volume = header.findIndex(v => ['volume', 'tickvol', 'tickvolume'].includes(v));
  return validateCandles(lines.slice(1).map((line, index) => {
    const cells = split(line);
    if ([open, high, low, close].some(i => !cells[i])) throw new Error(`Missing price on CSV line ${index + 2}.`);
    const stamp = date >= 0 ? `${cells[date]}${time >= 0 ? ' ' + cells[time] : ''}` : cells[time];
    return { time: parseTime(stamp || ''), open: Number(cells[open]), high: Number(cells[high]), low: Number(cells[low]), close: Number(cells[close]), volume: volume >= 0 ? Number(cells[volume]) || 0 : 0 };
  }));
}

export function chartScale(rows: Candle[], extra: number[] = [], minPadding = 3): Scale {
  let low = Infinity, high = -Infinity;
  for (const row of rows) { low = Math.min(low, row.low); high = Math.max(high, row.high); }
  for (const p of extra) if (Number.isFinite(p) && p > 0) { low = Math.min(low, p); high = Math.max(high, p); }
  if (!Number.isFinite(low)) { low = 4100; high = 4200; }
  const padding = Math.max(minPadding, (high - low) * .12);
  return { low: Math.max(.01, low - padding), high: high + padding, top: 30, bottom: 470 };
}

export function pickAnchor(x: number, y: number, rows: Candle[], left: number, right: number, scale: Scale, snap: boolean, decimals = 2): Anchor {
  const i = clamp(Math.floor((clamp(x, left, right) - left) / (right - left) * rows.length), 0, rows.length - 1);
  const row = rows[i];
  let value = yPrice(y, scale);
  if (snap) value = Math.abs(value - row.high) <= Math.abs(value - row.low) ? row.high : row.low;
  return { time: row.time, price: snap ? value : Number(value.toFixed(decimals)) };
}

// Offline practice only. Never used as a replacement for a failed market request.
export function demoCandles(timeframe: Timeframe = 'M5'): Candle[] {
  let seed = 902;
  const random = () => { seed = (1664525 * seed + 1013904223) >>> 0; return seed / 4294967296; };
  const rows: Candle[] = [];
  const period = PERIOD[timeframe];
  const lastTime = Math.floor((Date.UTC(2026, 9, 7, 12) - 1) / period) * period;
  let last = 4138;
  for (let i = 0; i < 720; i++) {
    const trend = 4144 + Math.sin(i / 67) * 30 + Math.sin(i / 18) * 8;
    const close = Number((last + (trend - last) * .18 + (random() - .5) * 5).toFixed(2));
    rows.push({ time: lastTime - (719 - i) * period, open: last, close, high: Number((Math.max(last, close) + random() * 3).toFixed(2)), low: Number((Math.min(last, close) - random() * 3).toFixed(2)) });
    last = close;
  }
  return rows;
}
